import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

/**
 * Cookie name written by the provider to persist the user's resolved theme
 * across logouts. Backend session handlers can read this to seed the login
 * page's theme. Public constant so consumers can introspect it.
 */
export const THEME_HINT_COOKIE = 'ia_theme_hint'

interface ThemeContextValue {
  /**
   * The resolved theme actually applied to the DOM (`light` | `dark`).
   * Bind UI to this for "is the page dark right now" decisions
   * (e.g., choosing an SVG variant).
   */
  theme: ResolvedTheme
  /**
   * The user's preference (`light` | `dark` | `system`).
   *
   * NOTE — first-paint asymmetry: the cookie hint stores only the resolved
   * theme, not the preference. On initial mount `preference` is always
   * `'system'` until either (a) `setPreference()` is called by user
   * interaction, or (b) `applyFromSession()` hydrates it from the backend
   * session. `theme` is correctly hydrated from cookie before paint.
   *
   * Bind UI to `theme` for what's currently rendered. Bind to `preference`
   * only inside controls that explicitly let the user pick "System"
   * (e.g., ThemeToggle), and accept that the control briefly defaults to
   * "System" until backend session preference loads.
   */
  preference: ThemePreference
  /** Toggle between light and dark (skips 'system') */
  toggleTheme: () => void
  /** Set the preference explicitly. Used by ThemeToggle and backend session sync. */
  setPreference: (pref: ThemePreference) => void
  /** Apply a preference from the backend session (alias of setPreference, kept for clarity) */
  applyFromSession: (pref: ThemePreference) => void
  /** Reset to system preference (called on logout). Cookie hint is preserved. */
  resetToSystem: () => void
  /** Wire a persistence callback (called whenever preference changes). dp-admin sets this from AdminAuthContext to PUT preference to the backend. */
  _setPersist: (fn: (pref: ThemePreference) => void) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const HINT_MAX_AGE = 30 * 24 * 60 * 60

function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') {
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ) {
      return 'dark'
    }
    return 'light'
  }
  return pref
}

function applyThemeClass(resolved: ResolvedTheme) {
  const root = document.documentElement
  if (resolved === 'dark') root.classList.add('dark')
  else root.classList.remove('dark')
}

const TRANSITION_STYLE_ID = 'ia-theme-transition'

function applyTransition() {
  // Respect users who opt out of motion.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

  // StrictMode runs effects twice on first mount — dedupe the style node.
  document.getElementById(TRANSITION_STYLE_ID)?.remove()

  const style = document.createElement('style')
  style.id = TRANSITION_STYLE_ID
  // Scoped to the properties that actually re-paint on theme flip.
  // Avoids forcing a transition on every node's box-shadow/fill/stroke,
  // which is a known main-thread cost on dense pages.
  style.textContent = `*, *::before, *::after { transition: background-color 400ms ease, color 200ms ease, border-color 300ms ease !important; }`
  document.head.appendChild(style)
  setTimeout(() => style.remove(), 500)
}

function writeThemeHint(resolved: ResolvedTheme) {
  document.cookie = `${THEME_HINT_COOKIE}=${resolved};path=/;max-age=${HINT_MAX_AGE};SameSite=Lax`
}

function readThemeHint(): ResolvedTheme | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${THEME_HINT_COOKIE}=(dark|light)`))
  return match ? (match[1] as ResolvedTheme) : null
}

function getInitialTheme(): ResolvedTheme {
  return readThemeHint() ?? resolveTheme('system')
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>('system')
  const [resolved, setResolved] = useState<ResolvedTheme>(getInitialTheme)
  const persistRef = useRef<((pref: ThemePreference) => void) | null>(null)
  const isFirstMount = useRef(true)

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false
      applyThemeClass(resolved)
      return
    }
    applyTransition()
    applyThemeClass(resolved)
  }, [resolved])

  useEffect(() => {
    if (preference !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    // setResolved triggers the [resolved] effect above which calls
    // applyThemeClass — no need to call it directly here.
    const handler = (e: MediaQueryListEvent) => setResolved(e.matches ? 'dark' : 'light')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [preference])

  const setPreference = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref)
    const next = resolveTheme(pref)
    setResolved(next)
    writeThemeHint(next)
    persistRef.current?.(pref)
  }, [])

  const toggleTheme = useCallback(() => {
    const next: ResolvedTheme = resolved === 'dark' ? 'light' : 'dark'
    setPreference(next)
  }, [resolved, setPreference])

  const applyFromSession = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref)
    const next = resolveTheme(pref)
    setResolved(next)
    writeThemeHint(next)
  }, [])

  const resetToSystem = useCallback(() => {
    setPreferenceState('system')
    const hint = readThemeHint()
    setResolved(hint ?? resolveTheme('system'))
  }, [])

  const _setPersist = useCallback((fn: (pref: ThemePreference) => void) => {
    persistRef.current = fn
  }, [])

  return (
    <ThemeContext.Provider
      value={{
        theme: resolved,
        preference,
        toggleTheme,
        setPreference,
        applyFromSession,
        resetToSystem,
        _setPersist,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>')
  return ctx
}
